import React, { useCallback, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import LogContextProvider from '$extensionSrc/components/LogContextProvider'
import Toast from '$extensionSrc/components/Toast'
import {
  extensionRedirectPath,
  paymentPath,
} from '$extensionSrc/consts/urlConsts'
import { getEnabledPlatforms } from '$extensionSrc/data/enabledPlatforms'
import { getHasAutomationsByPlatform } from '$extensionSrc/recommendations/recommendationRegistryApi'
import { PLATFORM } from '$extensionSrc/utils/enums'
import { getSortedPlatforms } from '$extensionSrc/utils/platformMetadataUtils'
import { client } from '$serverSrc/apolloClient'
import {
  startTrialMutation,
  redeemEnterpriseLicenseCodeMutation,
} from '$serverSrc/data/query'

import PlanOption from './PlanOption'
import PlatformRow from './PlatformRow'
import styles from './SelectPlanPage.module.scss'

const FULL_PRICE = 100

export default function SelectPlanPage({
  userId,
  pageMode,
  stripeCouponId,
  stripePromoCodePercentOff,
  stripePromoCode,
  organizationName,
}: {
  userId: number
  pageMode: 'TRIAL_OR_PLAN' | 'PLAN_ONLY'
  stripeCouponId: string | null
  stripePromoCode: string | null
  stripePromoCodePercentOff: number | null
  organizationName: string
}) {
  const [error, setError] = useState<string | undefined>()
  const [selectedOptionType, setSelectedOptionType] = useState<
    'TRIAL' | 'PLAN'
  >(pageMode === 'TRIAL_OR_PLAN' ? 'TRIAL' : 'PLAN')
  const [selectedTrialPlatform, setSelectedTrialPlatform] = useState<
    PLATFORM | undefined
  >()

  const [isNextPageLoading, setIsNextPageLoading] = useState(false)

  const [sendStartTrialMutation] = useMutation(startTrialMutation, {
    client,
    onError: (values) => {
      setError(values.graphQLErrors[0].message)
      setIsNextPageLoading(false)
    },
    onCompleted: () => {
      setIsNextPageLoading(false)
      window.location.assign(`${extensionRedirectPath}?trial=1`)
    },
  })

  const [sendRedeemEnterpriseLicenseCodeMutation] = useMutation(
    redeemEnterpriseLicenseCodeMutation,
    {
      client,
      onError: (values) => {
        setError(values.graphQLErrors[0].message)
        setIsNextPageLoading(false)
      },
      onCompleted: (data) => {
        setIsNextPageLoading(false)
        if (data.redeemEnterpriseLicenseCode.success) {
          // For enterprise plans, redirect to extension page with paid=1
          window.location.assign(`${extensionRedirectPath}?paid=1`)
        } else {
          // The mutation should not fail at this point, since the promo code and
          // enterprise plan have been validated on previous pages. If it does,
          // The promo code became invalid while this user was on the page. If they refresh,
          // they will be shown the regular select-plan page.
          setError(
            `The code ${stripePromoCode} is no longer valid. Please refresh and try again.`,
          )
        }
      },
    },
  )

  const onClickCta = useCallback(() => {
    switch (selectedOptionType) {
      case 'TRIAL':
        setIsNextPageLoading(true)
        sendStartTrialMutation({
          variables: {
            userId,
            platform: selectedTrialPlatform,
          },
        })
        break
      case 'PLAN':
        if (stripePromoCode && organizationName) {
          // redeem enterprise license code
          setIsNextPageLoading(true)
          sendRedeemEnterpriseLicenseCodeMutation({
            variables: {
              code: stripePromoCode,
            },
          })
        } else {
          window.location.assign(paymentPath)
        }
        break
      default:
    }
  }, [
    selectedOptionType,
    selectedTrialPlatform,
    sendStartTrialMutation,
    userId,
    stripePromoCode,
    sendRedeemEnterpriseLicenseCodeMutation,
    setIsNextPageLoading,
    organizationName,
  ])

  const topBannerText = useMemo(() => {
    if (organizationName) {
      return `Here's what you get from ${BuildConfig.TENANT_NAME} as a member of ${organizationName}`
    }

    if (stripePromoCode) {
      return `Get ${stripePromoCodePercentOff}% off with your promo code ${stripePromoCode}`
    }

    return null
  }, [organizationName, stripePromoCode, stripePromoCodePercentOff])

  const planOptionBannerText = useMemo(() => {
    if (organizationName) {
      return `Part of your ${organizationName} benefits`
    }

    if (stripePromoCode) {
      return `Save ${stripePromoCodePercentOff}% with ${stripePromoCode}`
    }

    return undefined
  }, [organizationName, stripePromoCode, stripePromoCodePercentOff])

  let planOptionDiscountPrice: number | undefined
  if (stripePromoCodePercentOff) {
    planOptionDiscountPrice = (1 - stripePromoCodePercentOff / 100) * FULL_PRICE
  }

  const eventProps = useMemo(
    () => ({
      component: 'SelectPlanPage',
      pageMode,
      selectedOptionType,
      selectedTrialPlatform,
      stripeCouponId,
      stripePromoCode,
      stripePromoCodePercentOff,
    }),
    [
      pageMode,
      selectedOptionType,
      selectedTrialPlatform,
      stripeCouponId,
      stripePromoCode,
      stripePromoCodePercentOff,
    ],
  )

  const enabledPlatforms = getEnabledPlatforms()

  return (
    <LogContextProvider {...eventProps}>
      <div className={styles.SelectPlanPage}>
        {topBannerText && (
          <div className={styles.discountBanner}>{topBannerText}</div>
        )}
        <div className={styles.container}>
          <div className={styles.titleSection}>
            {pageMode === 'TRIAL_OR_PLAN' && (
              <>
                <div className={styles.title}>Choose your plan</div>
                <div className={styles.subtitle}>
                  Try it out first, or go all in — it's up to you!
                </div>
              </>
            )}
            {pageMode === 'PLAN_ONLY' && (
              <div className={styles.title}>When you upgrade, you get:</div>
            )}
          </div>
          <div className={styles.selectionSection}>
            {pageMode === 'TRIAL_OR_PLAN' && (
              <>
                <PlanOption
                  benefitList={[
                    'Full access to 1 platform of your choice',
                    'Checklist automations on 1 platform',
                    'Content cleanup tools on 1 platform',
                  ]}
                  checked={selectedOptionType === 'TRIAL'}
                  discountBannerText={undefined}
                  discountPrice={undefined}
                  fullPrice={undefined}
                  onClick={() => {
                    setSelectedOptionType('TRIAL')
                  }}
                  subtitle="Free — no credit card required"
                  title="7-day trial"
                />
                <PlanOption
                  benefitList={[
                    `Full access to all (${enabledPlatforms.length}) platforms`,
                    'Unlimited use of automations and tools',
                    'Monthly autoscans and reporting',
                  ]}
                  checked={selectedOptionType === 'PLAN'}
                  discountBannerText={planOptionBannerText}
                  discountPrice={planOptionDiscountPrice}
                  fullPrice={FULL_PRICE}
                  onClick={() => {
                    setSelectedOptionType('PLAN')
                  }}
                  subtitle="Billed annually"
                  title="Personal plan"
                />
              </>
            )}
            {pageMode === 'PLAN_ONLY' && (
              <PlanOption
                benefitList={[
                  `Full access to all (${enabledPlatforms.length}) platforms`,
                  'Unlimited use of automations and tools',
                  'Monthly autoscans and reporting',
                ]}
                checked={undefined}
                discountBannerText={planOptionBannerText}
                discountPrice={planOptionDiscountPrice}
                fullPrice={FULL_PRICE}
                onClick={undefined}
                subtitle="Billed annually"
                title="Personal plan"
              />
            )}
          </div>
          <div className={styles.platformListSection}>
            <div className={styles.platformListHeader}>
              {selectedOptionType === 'TRIAL' && (
                <div className={styles.platformListTitle}>
                  Choose a platform for your free trial:
                </div>
              )}
              {selectedOptionType === 'PLAN' && (
                <>
                  <div className={styles.platformListTitle}>
                    🎉 You'll have full access to all platforms:
                  </div>
                  <div className={styles.platformListDescription}>
                    On each platform, you’ll get a privacy and security
                    checklist with automations, unlimited use of tools, and
                    monthly autoscans.
                  </div>
                </>
              )}
            </div>
            <div className={styles.platformRows}>
              {getSortedPlatforms(enabledPlatforms)
                .filter(getHasAutomationsByPlatform)
                .map((platform) => (
                  <PlatformRow
                    key={platform}
                    platform={platform}
                    checked={
                      selectedOptionType === 'TRIAL'
                        ? selectedTrialPlatform === platform
                        : undefined
                    }
                    onClick={
                      selectedOptionType === 'TRIAL'
                        ? () => {
                            setSelectedTrialPlatform(platform)
                          }
                        : undefined
                    }
                    showCheckbox={selectedOptionType === 'TRIAL'}
                  />
                ))}
            </div>
          </div>
          <div className={clsx(styles.ctaFooter, 'primary-shadow')}>
            <ActionButton
              ariaLabel={
                selectedOptionType === 'TRIAL' ? 'Start your trial' : 'Continue'
              }
              className={styles.cta}
              type={ActionButtonType.PRIMARY}
              size={ActionButtonSize.MEDIUM}
              onClick={onClickCta}
              disabled={
                (selectedOptionType === 'TRIAL' && !selectedTrialPlatform) ||
                isNextPageLoading
              }
            >
              {selectedOptionType === 'TRIAL' && 'Start your trial'}
              {selectedOptionType === 'PLAN' && 'Continue'}
            </ActionButton>
          </div>
        </div>
        {error && <Toast text={error} color="red" />}
      </div>
    </LogContextProvider>
  )
}
